import { useEffect, useState } from 'react'
import { ShieldCheck, Trash2 } from 'lucide-react'
import {
  listDisclosureKeypairs,
  revokeDisclosureKeypair,
  type DisclosureKeypairItem,
} from '../../services/api'
import { loadKeypair, exportPublicKeyHex } from '../../services/disclosureKeys'
import ConfirmDialog from '../shared/ConfirmDialog'

interface Props {
  orgId: string
}

function formatDate(iso?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

export default function ManageDevicesPanel({ orgId }: Props) {
  const [keys, setKeys] = useState<DisclosureKeypairItem[] | null>(null)
  const [localKeyHex, setLocalKeyHex] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pendingRevokeHex, setPendingRevokeHex] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)

  async function refresh() {
    setLoading(true)
    setError(null)
    try {
      const items = await listDisclosureKeypairs()
      setKeys(items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load registered devices')
      setKeys([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    async function init() {
      // Resolve local IndexedDB key (best-effort — fingerprint is purely informational)
      try {
        const kp = await loadKeypair(orgId)
        if (kp) {
          const hex = await exportPublicKeyHex(kp.publicKey)
          if (!cancelled) setLocalKeyHex(hex)
        }
      } catch {
        // No local key available — that's fine, "This device" badge just won't show
      }
      if (!cancelled) await refresh()
    }
    init()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  async function handleConfirmRevoke() {
    if (!pendingRevokeHex) return
    setRevoking(true)
    setError(null)
    try {
      await revokeDisclosureKeypair(pendingRevokeHex)
      // Optimistically remove from local state, then refetch for canonical view
      setKeys((prev) => (prev || []).filter((k) => k.public_key_hex !== pendingRevokeHex))
      setPendingRevokeHex(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke device')
    } finally {
      setRevoking(false)
    }
  }

  const activeCount = keys?.length ?? 0
  const isLastKey = activeCount === 1
  const lastKeyWarning = isLastKey
    ? ' This is your organisation’s only active signing key — signing will be locked until a new device registers.'
    : ''

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <ShieldCheck size={18} className="text-iflow-500" />
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Registered signing devices</h3>
          <p className="text-xs text-gray-500">
            Each device that signs disclosure commitments has its own Ed25519 key. Revoking a device blocks new signatures from it; previously-signed commitments stay verified (per &sect;4.5 / Claim 3).
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && (
        <div className="text-sm text-gray-500 py-4">Loading registered devices…</div>
      )}

      {!loading && keys && keys.length === 0 && (
        <div className="text-sm text-gray-500 py-4 italic">No registered devices yet.</div>
      )}

      {!loading && keys && keys.length > 0 && (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {keys.map((k) => {
            const isThisDevice = localKeyHex !== null && localKeyHex === k.public_key_hex
            const shortFingerprint = k.public_key_hex.slice(0, 16)
            return (
              <li key={k.public_key_hex} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono text-gray-700">{shortFingerprint}…</code>
                    {isThisDevice && (
                      <span className="inline-block px-2 py-0.5 text-[10px] font-medium rounded-full bg-iflow-100 text-iflow-700">
                        This device
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Registered {formatDate(k.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => setPendingRevokeHex(k.public_key_hex)}
                  className="ml-3 flex items-center gap-1 px-2 py-1 text-xs text-red-600 border border-red-200 rounded hover:bg-red-50"
                  aria-label={`Revoke device ${shortFingerprint}`}
                >
                  <Trash2 size={12} /> Revoke
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <ConfirmDialog
        open={pendingRevokeHex !== null}
        variant="danger"
        title="Revoke this device?"
        message={`Revoke this device? It will no longer be able to sign new commitments. Historical signatures remain valid (per §4.5 / Claim 3).${lastKeyWarning}`}
        confirmLabel={revoking ? 'Revoking…' : 'Revoke device'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmRevoke}
        onCancel={() => setPendingRevokeHex(null)}
      />
    </div>
  )
}
